import React, { useEffect, useMemo, useState } from "react";
import LeftBar from "./leftBar";
import RightBar from "./rightBar";
import TopBar from "./topBar";
import { useDispatch, useSelector } from "react-redux";
import { setIsRightProfileOpen } from "../../store/common/commonSlice";
import {
  useGetPendingOrderCRMQuery,
  useGetPopupNotificationsQuery,
} from "../../store/common/commonApiSlice";
import classes from "./styles.module.scss";

const SKIP_DURATION_DAYS = 7;

const Layout = (props) => {
  const dispatch = useDispatch();
  const isRightProfileOpen = useSelector(
    (state) => state.common.isRightProfileOpen
  );
  const { data } = useGetPendingOrderCRMQuery();
  const { data: popupData } = useGetPopupNotificationsQuery();

  const [showPopup, setShowPopup] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const popupSlides = useMemo(() => {
    if (!popupData?.notification) return [];
    const now = new Date();
    return popupData.notification
      .filter((item) => {
        if (!item.is_active) return false;
        const start = new Date(item.start_date);
        const end = new Date(item.end_date);
        return now >= start && now <= end;
      })
      .sort((a, b) => a.order - b.order);
  }, [popupData]);

  const popupVersion = useMemo(() => {
    if (!popupSlides.length) return null;
    return "popup_" + popupSlides.map((s) => s.id).join("_");
  }, [popupSlides]);

  useEffect(() => {
    if (!popupSlides.length || !popupVersion) return;

    const savedData = localStorage.getItem("student_popup_data");

    if (!savedData) {
      setShowPopup(true);
      return;
    }

    try {
      const { version, skippedAt } = JSON.parse(savedData);

      if (version !== popupVersion) {
        setShowPopup(true);
        return;
      }

      if (skippedAt) {
        const daysSinceSkip =
          (Date.now() - skippedAt) / (1000 * 60 * 60 * 24);
        if (daysSinceSkip >= SKIP_DURATION_DAYS) {
          setShowPopup(true);
        }
      }
    } catch (error) {
      setShowPopup(true);
    }
  }, [popupSlides, popupVersion]);

  const handleClose = () => setShowPopup(false);

  const handleSkip = () => {
    const data = {
      version: popupVersion,
      skippedAt: Date.now(),
    };
    localStorage.setItem("student_popup_data", JSON.stringify(data));
    setShowPopup(false);
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % popupSlides.length);
  };

  const handlePrevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + popupSlides.length) % popupSlides.length
    );
  };

  useEffect(() => {
    if (!showPopup || isPaused || !popupSlides.length) return;

    const autoSlideInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % popupSlides.length);
    }, 4000);

    return () => clearInterval(autoSlideInterval);
  }, [showPopup, currentSlide, isPaused, popupSlides.length]);

  const handleClick = () => {
    dispatch(setIsRightProfileOpen(!isRightProfileOpen));
  };

  useEffect(() => {
    if (isRightProfileOpen) {
      window.scrollTo({ left: 800, behavior: "smooth" });
    }
  }, [isRightProfileOpen]);

  return (
    <div style={{ display: "flex" }}>
      <LeftBar />
      <TopBar validTill={data?.validTill} handleClick={handleClick}>
        {props.children}
      </TopBar>
      {isRightProfileOpen && <RightBar handleClick={handleClick} />}

      {showPopup && popupSlides.length > 0 && (
        <div className={classes.popupOverlay}>
          <div
            className={classes.popupContainer}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <button className={classes.closeIcon} onClick={handleClose}>
              ✕
            </button>
            <div className={classes.sliderWrapper}>
              {popupSlides.length > 1 && (
                <button className={classes.arrowBtn} onClick={handlePrevSlide}>
                  ‹
                </button>
              )}
              <div className={classes.slideContainer}>
                <img
                  src={popupSlides[currentSlide]?.image}
                  alt={
                    popupSlides[currentSlide]?.alt ||
                    popupSlides[currentSlide]?.title
                  }
                  className={classes.popupImage}
                />
              </div>
              {popupSlides.length > 1 && (
                <button className={classes.arrowBtn} onClick={handleNextSlide}>
                  ›
                </button>
              )}
            </div>
            {popupSlides.length > 1 && (
              <div className={classes.dotsContainer}>
                {popupSlides.map((slide, index) => (
                  <button
                    key={slide.id}
                    className={`${classes.dot} ${index === currentSlide ? classes.activeDot : ""
                      }`}
                    onClick={() => setCurrentSlide(index)}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            )}
            <div className={classes.popupButtons}>
              <button onClick={handleClose}>Close</button>
              <button onClick={handleSkip}>Don't show again</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;

